import React, { useEffect, useState } from "react";
import { connect, useDispatch } from "react-redux";
import { compose } from "redux";
import { withTranslation } from "react-i18next";
import { Divider, Popover, Select, Space, Tooltip, Typography } from "antd";
import { createStructuredSelector } from "reselect";
import styled from "styled-components";
import { useHistory, useParams } from "react-router-dom";
const { Option } = Select;

import { selectPatientDetails } from "../../../store/selector";
import { fontWeights, sizes } from "../../../constant/styles";
import {
  GRAY_400,
  GRAY_600,
  GRAY_900,
  GRAY_300,
  GRAY7,
  WARNING_50,
  WARNING_100,
  WARNING_200,
  WARNING_300,
  WARNING_500,
  WARNING_600,
  BLACK,
  WHITE
} from "../../../constant/colors";
import { ReactComponent as HazardTriangle } from "../../../assets/svg/hazardTriangle.svg";
import ArrowBack from "../../../assets/svg/ArrowBack";
import { numberWithCommas } from "../../../utils/string";
import { PatientCoverageDetails } from "../PatientTopSection/PatientCoverageDetails";
import { QuestionCircleOutlined } from "@ant-design/icons";
import { formatPhoneNumber } from "../../../utils/formaters";
import { ShorterTextWithTooltip } from "../../ShorterText";
import { makeStyleWrapper } from "../../../utils/styles";
import ErrorMessage from "../../customComponent/customMessages/ErrorMessage";
import SuccessMessage from "../../customComponent/customMessages/SuccessMessage";
import ACTIONS from "../../../store/action";
import { updatePreferredLanguageAPI } from "../../../api/api";
import { inputStyle } from "../PatientTopSection";
import { MoreDetailsTextStyle } from "./styles";
import { AlternateContactOptions } from "../../../constant/patientCaregiver";
import { ALL_LANGUAGES } from "../../../constant/languages";

const { Text } = Typography;
const primaryTextStyle = { fontSize: sizes.medium, color: GRAY_900, fontWeight: fontWeights.regular };
const secondaryTextStyle = { fontSize: sizes.small, color: GRAY_900, fontWeight: fontWeights.semibold };

const wrapDropdown = makeStyleWrapper(`
  .ant-select-item {
   font-size:${sizes.small};
   color:${GRAY_900}
  }
`);

const CustomSelect = styled(Select)`
  div.ant-select-selector {
    padding-left: 0 !important;
    padding-right: 12 !important;
    background-color: ${(props) =>
      props.style.hoverPreferredlanguage || props.style.isFetching ? `${GRAY7} !important` : `transparent !important`};
  }
  .ant-select-selection-item,
  .ant-select-selection-placeholder {
    margin-top: 1px;
  }
  .ant-select-arrow {
    margin-top: -4px;
  }
  ${(props) => ({ ...props.inputStyle })};
`;

const StyledCustomSelect = styled(CustomSelect)`
  .ant-select-selection-search {
    left: 0px !important;
  }
`;

const FplWarningButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 8px;
  border: 1px solid ${WARNING_200};
  background: ${WARNING_50};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${WARNING_100};
    border: 1px solid ${WARNING_300};
  }

  &:focus {
    background: ${WARNING_50};
    border: 1px solid ${WARNING_200};
    box-shadow: 0px 0px 0px 2px ${WARNING_100};
    outline: none;
  }

  span {
    font-family: "Open Sans";
    font-weight: 600;
    font-size: 12px;
    line-height: 150%;
    color: ${WARNING_600};
  }
`;

const FplTooltipContent = styled.div`
  font-family: "Open Sans";
  font-weight: 600;
  font-size: 12px;
  line-height: 150%;
  text-align: center;
  color: ${BLACK};
`;

const StyledHazardTriangle = styled(HazardTriangle)`
  path {
    stroke: ${WARNING_500};
  }
`;

const PatientPersonalDetailsComponent = ({ t, patientDetails }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { patientId, journeyId } = useParams();
  const [preferredLanguage, setPreferredLanguage] = useState("");
  const [isFetching, setIsFetching] = useState(false);
  const [hoverPreferredlanguage, setHoverPreferredlanguage] = useState(false);

  const {
    alternateContactOption,
    emailCaregiver,
    fullNameCaregiver,
    phoneCaregiver,
    relationToPatientCaregiver,
    phoneNumber,
    homeNumber
  } = patientDetails || {};

  const hasAlternateContact = !!(emailCaregiver || fullNameCaregiver || phoneCaregiver || relationToPatientCaregiver);

  const getDisplayedPhoneNumber = () => {
    if (patientDetails) {
      const displayedPhoneNumber = phoneNumber || homeNumber;
      if (displayedPhoneNumber) {
        return formatPhoneNumber(displayedPhoneNumber);
      }
    }
    return "--";
  };
  useEffect(() => {
    setPreferredLanguage(patientDetails?.preferredLanguage);
  }, [patientDetails]);

  const handleUpdatePreferredLanguage = async (preferredLanguage) => {
    setPreferredLanguage(preferredLanguage);
    try {
      setIsFetching(true);
      const result = await updatePreferredLanguageAPI(patientDetails.id, preferredLanguage);
      if (result && result.data && result.data.success) {
        SuccessMessage(t("patientTopSection.success_message_update_preferred_language"));
        dispatch(ACTIONS.updatePatientDetailsAction({ ...patientDetails, preferredLanguage }));
      } else {
        ErrorMessage(t("patientTopSection.failed_message_update_preferred_language"));
      }
    } finally {
      setIsFetching(false);
    }
  };

  const totalHouseholdSize = patientDetails
    ? patientDetails.householdSize ||
      Number(patientDetails.householdSizeAdults ?? 0) + Number(patientDetails.householdSizeChildren ?? 0)
    : null;

  const isFplMissing = !(patientDetails?.fpl !== null && patientDetails?.fpl !== undefined && totalHouseholdSize > 0);

  const handleAddFplClick = () => {
    history.push(`/patient/${patientId}/journey/${journeyId}/edit`, {
      scrollToSection: "manage-patient-details-financial-income"
    });
  };

  return (
    <Space direction="vertical" size={0}>
      <Text style={{ ...secondaryTextStyle, color: GRAY_600 }}>{t("patientTopSection.contact_details")}</Text>
      <div
        style={{
          padding: "0px 0px 0px 10px",
          marginLeft: "-10px"
        }}
      >
        <Space>
          <Text style={secondaryTextStyle}>{t("patientTopSection.phone_number")}:</Text>
          <Text style={primaryTextStyle} id="contact_details_phone_number">
            {getDisplayedPhoneNumber()}
          </Text>
        </Space>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "auto 1fr",
            alignItems: "center",
            gap: "2px",
            width: "100%"
          }}
        >
          <Text style={{ ...secondaryTextStyle, whiteSpace: "nowrap" }}>
            {t("patientTopSection.preferred_language")}:
          </Text>
          <StyledCustomSelect
            id="preferred_language"
            loading={isFetching}
            bordered={false}
            style={{
              ...inputStyle,
              color: GRAY_900,
              fontSize: sizes.medium,
              fontWeights: fontWeights.regular,
              marginLeft: "5px",
              marginTop: "-3px",
              isFetching,
              hoverPreferredlanguage,
              maxWidth: "none",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              minWidth: "0px"
            }}
            onMouseEnter={() => setHoverPreferredlanguage(true)}
            onMouseLeave={() => setHoverPreferredlanguage(false)}
            value={preferredLanguage}
            showSearch={true}
            dropdownMatchSelectWidth={false}
            showArrow={hoverPreferredlanguage || isFetching}
            allowClear={true}
            disabled={isFetching}
            getPopupContainer={(trigger) => trigger.parentNode}
            placeholder={<Text style={{ color: GRAY_400, fontWeight: 400 }}>{t("select")}</Text>}
            dropdownRender={wrapDropdown}
            onChange={(value) => handleUpdatePreferredLanguage(value)}
          >
            {ALL_LANGUAGES.map((p, index) => (
              <React.Fragment key={p.value}>
                <Option value={p.value}>{p.label}</Option>
                {index === 4 && (
                  <Option disabled key="divider" style={{ cursor: "default" }}>
                    --------------------
                  </Option>
                )}
              </React.Fragment>
            ))}
          </StyledCustomSelect>
        </div>

        {hasAlternateContact && (
          <Space>
            <Popover
              trigger="click"
              getPopupContainer={(trigger) => trigger.parentNode}
              placement="rightTop"
              overlayInnerStyle={{
                minWidth: "220px",
                padding: "0px 12px",
                borderRadius: "8px",
                boxShadow: " 0px 4px 4px 0px #00000040"
              }}
              content={
                <Space size={0} direction="vertical">
                  <Space size={2} direction="vertical">
                    {alternateContactOption && (
                      <Text style={{ ...secondaryTextStyle, color: GRAY_600 }}>
                        {Object.values(AlternateContactOptions)
                          .find((alternateContact) => alternateContact.value == alternateContactOption)
                          .labelPatientTopSection(t) + ":"}
                      </Text>
                    )}
                  </Space>

                  <Space>
                    <Text style={secondaryTextStyle}>{t("patientTopSection.name")}:</Text>
                    <Text
                      style={{ ...primaryTextStyle, display: "flex", maxWidth: "400px", whiteSpace: "pre-wrap" }}
                      id="alternate_contact_name"
                    >
                      {fullNameCaregiver || ""}
                    </Text>
                  </Space>

                  <Space>
                    <Text style={secondaryTextStyle}>{t("patientTopSection.relation_to_patient")}:</Text>
                    <Text style={primaryTextStyle} id="alternate_contact_relation_to_patient">
                      {relationToPatientCaregiver || ""}
                    </Text>
                  </Space>

                  <Space>
                    <Text style={secondaryTextStyle}>{t("patientTopSection.phone_number")}:</Text>
                    <Text style={{ ...primaryTextStyle, color: GRAY_900 }} id="alternate_contact_phone_number">
                      {phoneCaregiver ? formatPhoneNumber(phoneCaregiver) : ""}
                    </Text>
                  </Space>

                  <Space>
                    <Text style={secondaryTextStyle}>{t("patientTopSection.email")}:</Text>
                    <Text style={primaryTextStyle} id="alternate_contact_email">
                      {emailCaregiver || ""}
                    </Text>
                  </Space>
                </Space>
              }
            >
              <MoreDetailsTextStyle id="alternate_contact_details">
                + {t("patientTopSection.alternate_contact_details")}
              </MoreDetailsTextStyle>
            </Popover>
          </Space>
        )}

        <Divider style={{ background: GRAY_300, marginTop: "12px", marginBottom: "5px" }} />
      </div>
      <Space>
        <Text style={secondaryTextStyle}>{t("patientTopSection.financial")}:</Text>
        {isFplMissing ? (
          <Tooltip
            title={<FplTooltipContent>{t("patientTopSection.add_fpl_tooltip")}</FplTooltipContent>}
            placement="top"
            color={WHITE}
            overlayStyle={{
              width: 240
            }}
            overlayInnerStyle={{
              backgroundColor: WHITE,
              borderRadius: 8,
              padding: "8px 12px",
              minHeight: 52
            }}
          >
            <FplWarningButton id="add_fpl_button" onClick={handleAddFplClick}>
              <StyledHazardTriangle style={{ width: 16, height: 16 }} />
              <span>{t("patientTopSection.add_fpl")}</span>
              <ArrowBack color={WARNING_600} rotate={true} />
            </FplWarningButton>
          </Tooltip>
        ) : (
          <>
            <Text style={primaryTextStyle}>
              {t("patientTopSection.fpl")} {parseInt(patientDetails.fpl)}%
            </Text>
            <Popover
              content={
                <Space size={16} style={{ width: "100%", padding: "0 12px" }}>
                  <Space direction="vertical">
                    <Text style={{ fontSize: sizes.medium }}>{t("total_income_yearly")}</Text>
                    <Text style={{ fontSize: sizes.medium }}>{t("total_household_size")}</Text>
                  </Space>
                  <Space direction="vertical">
                    <Text>
                      {patientDetails && patientDetails.income !== 0
                        ? `$${numberWithCommas(patientDetails.income)}`
                        : "--"}
                    </Text>
                    <Text>{(patientDetails && totalHouseholdSize) || "--"}</Text>
                  </Space>
                </Space>
              }
              trigger="hover"
              placement="bottom"
            >
              <QuestionCircleOutlined
                id="fpl_question_mark"
                style={{ display: "flex", alignItems: "center", cursor: "pointer", color: GRAY_400 }}
              />
            </Popover>
          </>
        )}
      </Space>
      {patientDetails && patientDetails.employer && (
        <Space>
          <Text style={secondaryTextStyle}>{t("patientTopSection.employer")}:</Text>
          <ShorterTextWithTooltip text={patientDetails.employer} maxLength={15} textStyle={primaryTextStyle} />
        </Space>
      )}
      <PatientCoverageDetails />
    </Space>
  );
};

const mapStateToProps = createStructuredSelector({
  patientDetails: selectPatientDetails
});

const PatientPersonalDetails = compose(withTranslation(), connect(mapStateToProps))(PatientPersonalDetailsComponent);

export { PatientPersonalDetails };
